import React, { useState } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import PassportEmblem from 'components/PassportEmblem'
import JumpToPicker from 'features/dashboard/sections/JumpToPicker'
import { ENTITY_COLORS } from 'constants/entityColors'

const KIND_META = {
  building: {
    colour: ENTITY_COLORS.bp,
    kindLabel: 'Building Passport',
  },
  estate: {
    colour: ENTITY_COLORS.estate,
    kindLabel: 'Estate Passport',
  },
  portfolio: {
    colour: ENTITY_COLORS.portfolio,
    kindLabel: 'Portfolio Passport',
  },
}

const HINT = 'Jump to any portfolio, estate, building or unit'

const PassportSwitcher = ({ kind = 'building', name, subtitle, meta = [], size = 'lg', onSelectBp, className }) => {
  const kindMeta = KIND_META[kind] || KIND_META.building
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const detail = [subtitle, ...meta].filter(Boolean).join('  ·  ')

  const handleSelect = target => {
    setOpen(false)
    if (!target?.id) return
    if (target.type === 'portfolio') return navigate(`/portfolios/${target.id}`)
    if (target.type === 'estate') return navigate(`/estates/${target.id}`)
    if (target.type === 'unit' && target.parentId) {
      return navigate(`/bp-list/${target.parentId}/units/${target.id}`)
    }
    if (onSelectBp) return onSelectBp(target)
    navigate(`/bp-list/${target.id}`)
  }

  return (
    <>
      <Identity className={className} $colour={kindMeta.colour}>
        <EmblemSlot>
          <PassportEmblem kind={kind} size={size} showLabel={false} />
        </EmblemSlot>
        <Text>
          <Kind $colour={kindMeta.colour} $lg={size === 'lg'}>{kindMeta.kindLabel}</Kind>
          <NameBtn
            type='button'
            $colour={kindMeta.colour}
            $lg={size === 'lg'}
            onClick={() => setOpen(true)}
            title={HINT}
            aria-haspopup='dialog'
            aria-label={`${name} — ${HINT}`}
          >
            <NameText $lg={size === 'lg'}>{name}</NameText>
            <ExpandMoreRoundedIcon style={{ fontSize: size === 'lg' ? 28 : 22, flexShrink: 0 }} />
          </NameBtn>
          {detail && <Sub>{detail}</Sub>}
        </Text>
      </Identity>

      <JumpToPicker
        open={open}
        onClose={() => setOpen(false)}
        onSelect={handleSelect}
      />
    </>
  )
}

export default PassportSwitcher

const Identity = styled.span`
  && {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
    opacity: 1;
    font-size: inherit;
  }

  @media (max-width: 768px) {
    && {
      gap: 12px;
    }
  }
`



// The emblem's own wrapper is allowed to shrink, which let the tile spill over the title on
// narrow screens; keep its slot at full width.
const EmblemSlot = styled.span`
  && {
    display: inline-flex;
    flex-shrink: 0;
  }

  /* Smaller tile on phones: the full-size one dominated the header there */
  @media (max-width: 768px) {
    && [role='img'] {
      width: 48px;
      height: 48px;
      border-radius: 13px;
    }

    && svg {
      width: 32px;
      height: 32px;
    }
  }
`

const Text = styled.span`
  && {
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
    opacity: 1;
    font-size: inherit;
  }
`

const Kind = styled.span`
  && {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    line-height: 1.4;
    white-space: nowrap;
    opacity: 1;
    color: ${({ $colour }) => $colour};
  }
`

const NameBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  max-width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  font: inherit;
  ${({ $lg }) => $lg && 'font-size: 34px; font-weight: 600; letter-spacing: -0.02em; line-height: 1.15;'}
  color: inherit;

  @media (max-width: 768px) {
    ${({ $lg }) => $lg && 'font-size: 22px;'}
  }
  text-align: left;
  cursor: pointer;
  border-radius: 4px;
  transition: color 160ms ease;

  svg {
    color: ${({ $colour }) => $colour};
    opacity: 0.75;
    transition: transform 160ms ease;
  }

  &:hover {
    color: ${({ $colour }) => $colour};
  }

  &:hover svg {
    opacity: 1;
    transform: translateY(1px);
  }

  &:focus-visible {
    outline: 2px solid ${({ $colour }) => $colour};
    outline-offset: 3px;
  }
`

const NameText = styled.span`
  && {
    font-size: inherit;
    ${({ $lg }) => $lg && 'font-size: 34px;'}
    min-width: 0;

    @media (max-width: 768px) {
      ${({ $lg }) => $lg && 'font-size: 22px;'}
    }
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    opacity: 1;
  }
`

const Sub = styled.span`
  && {
    margin-top: 3px;
    font-size: 13.5px;
    font-weight: 400;
    line-height: 1.4;
    letter-spacing: 0;
    text-transform: none;
    color: #8A8F9C;
    opacity: 1;
  }

  @media (max-width: 768px) {
    && {
      font-size: 12.5px;
    }
  }
`
